import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './TooltipPortal.css';

const TOOLTIP_OFFSET = 8;

export function TooltipPortal() {
  const [state, setState] = useState({
    content: null,
    left: 0,
    top: 0,
    position: 'top', // 'top' or 'bottom'
    narrow: false
  });
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (state.content) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setVisible(false);
    }
  }, [state.content]);

  useEffect(() => {
    const show = (el) => {
      if (!el || !el.dataset.tooltip) return;
      triggerRef.current?.classList.remove('tooltip--portal-visible');
      triggerRef.current = el;
      el.classList.add('tooltip--portal-visible');
      const rect = el.getBoundingClientRect();
      const position = el.dataset.tooltipPosition || 'top';
      setState({
        content: el.dataset.tooltip,
        left: rect.left + rect.width / 2,
        top: position === 'bottom' ? rect.bottom + TOOLTIP_OFFSET : rect.top - TOOLTIP_OFFSET,
        position,
        narrow: el.classList.contains('tooltip--max-160')
      });
    };

    const hide = (el) => {
      if (el) el.classList.remove('tooltip--portal-visible');
      if (triggerRef.current) {
        triggerRef.current.classList.remove('tooltip--portal-visible');
        triggerRef.current = null;
      }
      setState((prev) => ({ ...prev, content: null }));
    };

    const handleMouseOver = (e) => {
      const trigger = e.target.closest?.('[data-tooltip]');
      if (trigger) show(trigger);
    };

    const handleMouseOut = (e) => {
      const trigger = e.target.closest?.('[data-tooltip]');
      if (!trigger) return;
      const related = e.relatedTarget;
      if (related && (trigger.contains(related) || tooltipRef.current?.contains(related))) return;
      hide(trigger);
    };

    const handleFocusIn = (e) => {
      const trigger = e.target.closest?.('[data-tooltip]');
      if (trigger) show(trigger);
    };

    const handleFocusOut = (e) => {
      const related = e.relatedTarget;
      if (related && tooltipRef.current?.contains(related)) return;
      hide(triggerRef.current);
    };

    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
      if (triggerRef.current) {
        triggerRef.current.classList.remove('tooltip--portal-visible');
        triggerRef.current = null;
      }
    };
  }, []);

  if (!state.content) return null;

  const tooltip = (
    <div
      ref={tooltipRef}
      className={`tooltip-portal tooltip-portal--${state.position} ${state.narrow ? 'tooltip-portal--max-160' : ''} ${visible ? 'tooltip-portal--visible' : ''}`}
      role="tooltip"
      style={{
        left: state.left,
        top: state.top
      }}
    >
      <span className="tooltip-portal__content">{state.content}</span>
      <span className="tooltip-portal__arrow" aria-hidden="true" />
    </div>
  );

  return createPortal(tooltip, document.body);
}
